import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import api, { fileUrl } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { MapPin, Package, Tag, Ruler, ArrowLeft, Gauge, BadgeCheck } from "lucide-react";
import { waLink } from "../lib/constants";

const PLACEHOLDER = "https://images.pexels.com/photos/3806252/pexels-photo-3806252.jpeg";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/marketplace"));
  }, [id, navigate]);

  if (!product) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-7xl mx-auto p-16 animate-pulse">Loading…</div></div>;

  const images = product.images && product.images.length ? product.images : null;
  const mainImg = images ? fileUrl(images[active]) : PLACEHOLDER;

  const sendInquiry = async () => {
    if (!user) { navigate("/login"); return; }
    setSending(true);
    try {
      await api.post("/inquiries", { product_id: product.product_id, message, contact_phone: phone });
      toast.success("Inquiry sent — the seller has been notified");
      setOpen(false);
      setMessage(""); setPhone("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const specs = [
    { icon: Tag, label: "Brand", value: product.brand },
    { icon: Ruler, label: "Size", value: product.size },
    { icon: Gauge, label: "Tread depth", value: product.tread_depth },
    { icon: Package, label: "Quantity", value: product.quantity },
    { icon: MapPin, label: "Location", value: product.location },
  ].filter((s) => s.value);

  const waContact = product.seller_whatsapp
    ? waLink(product.seller_whatsapp, `Hi, I'm interested in "${product.title}" on WILTOM TradeLink.`)
    : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-marketplace">
          <ArrowLeft className="h-4 w-4" /> Back to marketplace
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Gallery */}
          <div>
            <div className="aspect-square border border-foreground overflow-hidden bg-muted">
              <img src={mainImg} alt={product.title} className="h-full w-full object-cover"
                   onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} data-testid="product-main-image" />
            </div>
            {images && images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActive(i)}
                    className={`aspect-square border overflow-hidden ${i === active ? "border-primary" : "border-border"}`} data-testid={`thumb-${i}`}>
                    <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="label-caps text-primary">{product.category}</span>
              <Badge variant="outline" className="rounded-none uppercase text-[10px] tracking-wider">{product.condition}</Badge>
              {product.wiltom_verified && (
                <Badge className="rounded-none bg-primary text-white uppercase text-[10px] tracking-wider" data-testid="product-wiltom-verified">
                  <BadgeCheck className="h-3.5 w-3.5 mr-1" /> Official WILTOM Listing
                </Badge>
              )}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="product-title">{product.title}</h1>
            <div className="font-display font-black text-3xl text-primary mt-4" data-testid="product-price">£{Number(product.price).toLocaleString()}</div>
            <p className="text-sm text-muted-foreground mt-1">Sold by <strong className="text-foreground">{product.seller_name}</strong></p>

            <div className="grid grid-cols-2 gap-px bg-border border border-border mt-6">
              {specs.map((s, i) => (
                <div key={i} className="bg-white p-4">
                  <div className="flex items-center gap-2 label-caps text-muted-foreground"><s.icon className="h-3.5 w-3.5" />{s.label}</div>
                  <div className="font-medium mt-1">{s.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <span className="label-caps text-muted-foreground">Description</span>
              <p className="text-sm mt-2 whitespace-pre-line leading-relaxed">{product.description}</p>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="w-full mt-8 rounded-none bg-primary hover:bg-primary/90 h-12 text-base" data-testid="open-inquiry-btn">
                  Send inquiry to seller
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="font-display">Inquire about {product.title}</DialogTitle></DialogHeader>
                {!user && <p className="text-sm text-muted-foreground">Please <Link to="/login" className="text-primary">log in</Link> to send an inquiry.</p>}
                <div className="space-y-4">
                  <div>
                    <Label className="label-caps">Your message</Label>
                    <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                              className="rounded-none mt-2" placeholder="Quantity needed, delivery location, timeline…" data-testid="inquiry-message" />
                  </div>
                  <div>
                    <Label className="label-caps">Contact phone (optional)</Label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none mt-2 h-11" data-testid="inquiry-phone" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={sendInquiry} disabled={sending || !message || !user}
                          className="rounded-none bg-primary hover:bg-primary/90" data-testid="submit-inquiry-btn">
                    {sending ? "Sending…" : "Send inquiry"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {waContact && (
              <a href={waContact} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-contact-btn">
                <Button variant="outline" className="w-full mt-3 rounded-none h-12 text-base border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white">
                  Contact seller on WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
